import { TaskGroupBy as TaskListGroupBy } from "@common/types/task";
import { TaskColumn } from "@common/types/views/task-view-enums";
import { getTaskBoardAxisFields } from "@components/tasks/board/taskBoardFields";
import {
    ENTITY_LANE_BY,
    type TaskBoardLaneBy,
} from "@components/tasks/board/taskBoardLanes";
import { TASK_CARD_COMMENTS_FIELD } from "@components/tasks/board/taskCardColumns";
import { getTaskColumnName } from "@components/tasks/board/taskCardEdits";
import type {
    BoardAddPlacement,
    BoardCardFieldOption,
    BoardColumnDef,
    BoardMove,
    BoardPlacement,
    BoardViewState,
} from "@design-system/Board/Board.types";
import type { DemoTask } from "./fixtures/demoTasks";
import {
    getAxisValueIds,
    setAxisValue,
    TASK_AXIS_VALUES,
    type TaskAxis,
    type TaskAxisValue,
} from "./taskBoardAxes";
import { TASK_SORT_OPTIONS, type TaskSort } from "./taskBoardSort";

export type TaskColumnBy = Extract<
    TaskAxis,
    "stage" | "status" | "assignee" | "priority"
>;
export type TaskGroupBy = Extract<
    TaskAxis,
    "assignee" | "client" | "priority" | "entity"
>;

export const TASK_COLUMN_BY_OPTIONS: ReadonlyArray<{
    value: TaskColumnBy;
    label: string;
}> = [
    { value: "stage", label: "Stage" },
    { value: "status", label: "Status" },
    { value: "assignee", label: "Assignee" },
    { value: "priority", label: "Priority" },
];

export const TASK_GROUP_BY_OPTIONS: ReadonlyArray<{
    value: TaskGroupBy;
    label: string;
}> = [
    { value: "assignee", label: "Assignee" },
    { value: "client", label: "Client" },
    { value: "priority", label: "Priority" },
    { value: "entity", label: "Entity" },
];

const toFieldOption = <T extends TaskColumn>(id: T) => ({
    id,
    label: getTaskColumnName(id),
});

/**
 * Optional card fields, named as List's columns and the real board's gear
 * name them; the title and status pill always show.
 */
export const TASK_CARD_FIELD_OPTIONS = [
    toFieldOption(TaskColumn.DUE_DATE),
    toFieldOption(TaskColumn.PRIORITY),
    toFieldOption(TaskColumn.ENTITIES),
    toFieldOption(TaskColumn.CLOSE_STAGE),
    toFieldOption(TaskColumn.CLOSE),
    toFieldOption(TaskColumn.SERVICE_PERIOD),
    toFieldOption(TaskColumn.VISIBILITY),
    toFieldOption(TaskColumn.ESTIMATE),
    toFieldOption(TaskColumn.GROUP),
    toFieldOption(TaskColumn.CREATED_AT),
    toFieldOption(TaskColumn.CREATED_BY),
    toFieldOption(TaskColumn.COMPLETED_BY),
    { id: TASK_CARD_COMMENTS_FIELD, label: "Comments" },
] as const satisfies readonly BoardCardFieldOption[];

/** The optional card fields as the Display popover's column settings rows. */
export const TASK_CARD_FIELD_CONFIGS = Object.fromEntries(
    TASK_CARD_FIELD_OPTIONS.map(({ id, label }) => [id, { name: label }]),
);

/** Id of an optional card field. */
export type TaskCardFieldId = (typeof TASK_CARD_FIELD_OPTIONS)[number]["id"];

/** The real Tasks board's defaults on a firm workspace with stages and services. */
export const DEFAULT_TASK_BOARD_VIEW: BoardViewState = {
    columnBy: "status",
    groupBy: null,
    sort: "attention",
    cardFields: [
        TaskColumn.DUE_DATE,
        TaskColumn.PRIORITY,
        TaskColumn.ENTITIES,
        TaskColumn.CLOSE_STAGE,
        TaskColumn.CLOSE,
        TaskColumn.SERVICE_PERIOD,
        TaskColumn.VISIBILITY,
        TASK_CARD_COMMENTS_FIELD,
    ],
    isEmptyColumnsShown: true,
    collapsedColumnIds: [],
    collapsedLaneIds: [],
};

/** The view state's ids narrowed to the playground's own unions. */
export interface TaskBoardView {
    columnBy: TaskColumnBy;
    groupBy: TaskGroupBy | null;
    sort: TaskSort;
}

const findOption = <T extends string>(
    options: ReadonlyArray<{ value: T }>,
    value: string | null,
) => options.find((option) => option.value === value)?.value;

export const parseTaskBoardView = (state: BoardViewState): TaskBoardView => ({
    columnBy: findOption(TASK_COLUMN_BY_OPTIONS, state.columnBy) ?? "status",
    groupBy: findOption(TASK_GROUP_BY_OPTIONS, state.groupBy) ?? null,
    sort: findOption(TASK_SORT_OPTIONS, state.sort) ?? "attention",
});

export const createTaskPlacements =
    ({ columnBy, groupBy }: TaskBoardView) =>
    (task: DemoTask): readonly BoardPlacement[] =>
        getAxisValueIds(task, columnBy).flatMap((columnId): BoardPlacement[] =>
            groupBy === null
                ? [{ columnId, laneId: null }]
                : getAxisValueIds(task, groupBy).map((laneId) => ({
                      columnId,
                      laneId,
                  })),
        );

/** A move across columns or lanes changes the property each one shows. */
export const updateMovedTask = (
    task: DemoTask,
    { from, to }: Pick<BoardMove<DemoTask>, "from" | "to">,
    { columnBy, groupBy }: TaskBoardView,
): DemoTask => {
    const moved =
        from.columnId === to.columnId
            ? task
            : setAxisValue(task, columnBy, {
                  fromId: from.columnId,
                  toId: to.columnId,
              });
    return groupBy === null || to.laneId === null || from.laneId === to.laneId
        ? moved
        : setAxisValue(moved, groupBy, {
              fromId: from.laneId,
              toId: to.laneId,
          });
};

/** A draft created in a cell takes that column's and lane's values. */
export const placeDraft = (
    draft: DemoTask,
    placement: BoardPlacement,
    { columnBy, groupBy }: TaskBoardView,
) => {
    const placed = setAxisValue(draft, columnBy, {
        fromId: null,
        toId: placement.columnId,
    });
    return groupBy === null || placement.laneId === null
        ? placed
        : setAxisValue(placed, groupBy, {
              fromId: null,
              toId: placement.laneId,
          });
};

/**
 * Values with at least one of `tasks`. The board only changes `items` on
 * drop, so hidden columns and lanes stay hidden during a drag.
 */
const getValuesWithTasks = (
    axis: TaskAxis,
    tasks: readonly DemoTask[],
): TaskAxisValue[] => {
    const valueIds = new Set(
        tasks.flatMap((task) => getAxisValueIds(task, axis)),
    );
    return TASK_AXIS_VALUES[axis].filter(({ id }) => valueIds.has(id));
};

const HEADER_ICON_AXES: ReadonlySet<TaskAxis> = new Set(["status", "priority"]);

/** A column or lane header: icons for statuses and priorities only, as on the real board. */
const toHeader = (axis: TaskAxis, { id, title, icon }: TaskAxisValue) =>
    HEADER_ICON_AXES.has(axis) ? { id, title, icon } : { id, title };

/** Lanes with visible tasks, like Linear's default of hiding empty groups. */
export const getTaskLanes = (
    groupBy: TaskGroupBy | null,
    visibleTasks: readonly DemoTask[],
) =>
    groupBy === null
        ? undefined
        : getValuesWithTasks(groupBy, visibleTasks).map((value) =>
              toHeader(groupBy, value),
          );

const COLUMN_GROUP_BYS: Record<TaskColumnBy, TaskListGroupBy> = {
    stage: TaskListGroupBy.CLOSE_STAGE,
    status: TaskListGroupBy.STATUS,
    assignee: TaskListGroupBy.ASSIGNEE,
    priority: TaskListGroupBy.PRIORITY,
};

const LANE_BYS: Record<TaskGroupBy, TaskBoardLaneBy> = {
    assignee: TaskListGroupBy.ASSIGNEE,
    client: TaskListGroupBy.CLIENT,
    priority: TaskListGroupBy.PRIORITY,
    entity: ENTITY_LANE_BY,
};

/** The card fields the columns and lanes already show, which the real board leaves off its cards. */
export const getTaskAxisFields = ({
    columnBy,
    groupBy,
}: Pick<TaskBoardView, "columnBy" | "groupBy">) =>
    getTaskBoardAxisFields({
        groupBy: COLUMN_GROUP_BYS[columnBy],
        laneBy: groupBy === null ? null : LANE_BYS[groupBy],
    });

const NON_STAGE_ADD_PLACEMENTS: readonly BoardAddPlacement[] = [
    "header",
    "footer",
];

interface BuildTaskColumnsArgs {
    columnBy: TaskColumnBy;
    tasks: readonly DemoTask[];
    visibleTasks: readonly DemoTask[];
    isEmptyColumnsShown: boolean;
    /** Controls panel choice; applies to stage columns. */
    stageAddPlacements: Readonly<Record<string, readonly BoardAddPlacement[]>>;
}

/** Columns for `columnBy`, with unfiltered totals for the "3 of 7" header. */
export const buildTaskColumns = ({
    columnBy,
    tasks,
    visibleTasks,
    isEmptyColumnsShown,
    stageAddPlacements,
}: BuildTaskColumnsArgs): BoardColumnDef[] => {
    const totalCounts = new Map<string, number>();
    tasks.forEach((task) =>
        getAxisValueIds(task, columnBy).forEach((id) =>
            totalCounts.set(id, (totalCounts.get(id) ?? 0) + 1),
        ),
    );
    const values = isEmptyColumnsShown
        ? TASK_AXIS_VALUES[columnBy]
        : getValuesWithTasks(columnBy, visibleTasks);
    return values.map((value) => ({
        ...toHeader(columnBy, value),
        totalCount: totalCounts.get(value.id) ?? 0,
        addPlacements:
            columnBy === "stage"
                ? stageAddPlacements[value.id]
                : NON_STAGE_ADD_PLACEMENTS,
    }));
};
